import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="movement-validation"
export default class extends Controller {
  static targets = ["form", "status", "errors", "warnings", "preview", "submitButton", "batchField"]
  static values = {
    debounceMs: { type: Number, default: 500 }
  }

  connect() {
    console.log("[MovementValidation] Controller connected")
    this.debounceTimer = null
    this.formData = null
    this.isSubmitting = false
    this.disableSubmit()

    // Check batch requirement immediately when a zone select changes
    this.formTarget.addEventListener("change", (event) => {
      if (event.target.matches('[name="movement[origin_zone_id]"]') ||
          event.target.matches('[name*="[destination_zone_id]"]')) {
        this.checkBatchRequirement()
      }
      this.debouncedValidate()
    })

    // Validate on text/number input changes
    this.formTarget.addEventListener("input", () => {
      this.debouncedValidate()
    })

    this.formTarget.addEventListener("submit", () => this.showSubmitLoading())
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  debouncedValidate() {
    if (this.isSubmitting) return

    // Any relevant form change invalidates previous positive validation
    // until a fresh successful response is received.
    this.disableSubmit()

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    this.debounceTimer = setTimeout(() => {
      this.validate()
    }, this.debounceMsValue)
  }

  checkBatchRequirement() {
    if (!this.hasBatchFieldTarget) return

    const formData = new FormData(this.formTarget)
    const originZoneId = formData.get("movement[origin_zone_id]")

    // Collect all destination_zone_id values from the form
    const destinationZoneIds = []
    for (const [key, value] of formData.entries()) {
      if (key.match(/\[destination_zone_id\]$/) && value) {
        destinationZoneIds.push(value)
      }
    }

    if (!originZoneId || destinationZoneIds.length === 0) {
      this.setBatchFieldEnabled(false)
      return
    }

    const params = new URLSearchParams({
      origin_zone_id: originZoneId,
      destination_zone_id: destinationZoneIds[0]
    })

    fetch(`/movements/check_batch_requirement?${params}`, {
      headers: { "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
    })
      .then(r => r.json())
      .then(data => this.setBatchFieldEnabled(data.requires_batch_code))
      .catch(() => this.setBatchFieldEnabled(false))
  }

  setBatchFieldEnabled(enabled) {
    if (!this.hasBatchFieldTarget) return
    if (enabled) {
      this.batchFieldTarget.removeAttribute("disabled")
    } else {
      this.batchFieldTarget.setAttribute("disabled", "disabled")
      this.batchFieldTarget.value = ""
    }
  }

  validate() {
    console.log("[MovementValidation] Starting validation...")
    
    // Get form data
    const formData = new FormData(this.formTarget)
    
    // Convert FormData to object for JSON
    const movementData = {
      origin_zone_id: formData.get("movement[origin_zone_id]") || null,
      timestamp: formData.get("movement[timestamp]") || null,
      notes: formData.get("movement[notes]") || null,
      processing_batch_code: formData.get("movement[processing_batch_code]") || null,
      movement_destinations_attributes: this.extractDestinations(formData)
    }

    console.log("[MovementValidation] Movement data:", movementData)

    // Skip validation if required fields are missing
    // Filter out destinations without destination_zone_id
    const validDestinations = movementData.movement_destinations_attributes.filter(
      dest => this.destinationReadyForValidation(dest)
    )
    
    console.log("[MovementValidation] Valid destinations:", validDestinations)
    
    if (!this.readyForValidation(movementData.origin_zone_id, validDestinations)) {
      console.log("[MovementValidation] Skipping validation - missing required fields")
      this.hideAllFeedback()
      this.disableSubmit()
      return
    }
    
    // Use only valid destinations
    movementData.movement_destinations_attributes = validDestinations

    // Show loading state
    this.showLoading()

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    console.log("[MovementValidation] Sending validation request...")
    
    // Validate via AJAX
    fetch("/movements/validate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ movement: movementData })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP error! status: ${response.status}, body: ${text}`)
          })
        }
        return response.json()
      })
      .then(data => {
        console.log("[MovementValidation] Validation response:", data)
        this.handleValidationResponse(data)
      })
      .catch(error => {
        console.error("[MovementValidation] Error validating movement:", error)
        this.showError("Errore durante la validazione. Riprova.")
      })
  }

  /**
   * Validation for the 3 required trigger parameters:
   * - origin zone (from)
   * - destination zone (to)
   * - quantity (qta, positive)
   *
   * @param {string|null} originZoneId
   * @param {Array<Object>} validDestinations
   * @returns {boolean}
   */
  readyForValidation(originZoneId, validDestinations) {
    const hasOrigin = originZoneId && originZoneId.trim() !== ""
    const hasDestinationWithQuantity = validDestinations.length > 0

    return hasOrigin && hasDestinationWithQuantity
  }

  /**
   * Checks if a destination is ready for validation.
   * It verifies that the destination_zone_id is present and
   * the input_value is a positive number.
   *
   * @param {Object} destination - The destination object containing destination_zone_id and input_value.
   * @returns {boolean} True if the destination is valid for validation, false otherwise.
   */
  destinationReadyForValidation(destination) {
    const hasDestination = destination.destination_zone_id && destination.destination_zone_id.trim() !== ""
    const quantity = Number.parseFloat(destination.input_value)
    const hasPositiveQuantity = Number.isFinite(quantity) && quantity > 0

    return hasDestination && hasPositiveQuantity
  }

  extractDestinations(formData) {
    const destinations = []
    const destinationsMap = new Map()
    
    console.log("[MovementValidation] Extracting destinations from form...")
    
    // Rails nested attributes format: movement[movement_destinations_attributes][0][destination_zone_id]
    // Iterate through all form entries
    for (const [key, value] of formData.entries()) {
      // Match pattern: movement[movement_destinations_attributes][INDEX][FIELD]
      const match = key.match(/^movement\[movement_destinations_attributes\]\[(\d+)\]\[(\w+)\]$/)
      if (match) {
        const index = match[1]
        const field = match[2]
        
        console.log(`[MovementValidation] Found field: ${key} = ${value} (index: ${index}, field: ${field})`)
        
        if (!destinationsMap.has(index)) {
          destinationsMap.set(index, {})
        }
        
        const dest = destinationsMap.get(index)
        if (field === "destination_zone_id") {
          dest.destination_zone_id = value
        } else if (field === "input_type") {
          dest.input_type = value || "tons"
        } else if (field === "input_value") {
          dest.input_value = value || null
        }
      }
    }
    
    console.log("[MovementValidation] Destinations map:", destinationsMap)
    
    // Convert map to array, only including destinations with destination_zone_id
    for (const [index, dest] of destinationsMap.entries()) {
      if (dest.destination_zone_id) {
        destinations.push({
          destination_zone_id: dest.destination_zone_id,
          input_type: dest.input_type || "tons",
          input_value: dest.input_value || null
        })
      }
    }
    
    console.log("[MovementValidation] Extracted destinations:", destinations)
    
    return destinations
  }

  handleValidationResponse(data) {
    if (this.isSubmitting) return

    // Hide loading
    this.hideLoading()

    // Show errors if any
    if (data.errors && data.errors.length > 0) {
      this.showErrors(data.errors)
      this.disableSubmit()
    } else {
      this.hideErrors()
      this.enableSubmit()
    }

    // Show warnings if any
    if (data.warnings && data.warnings.length > 0) {
      this.showWarnings(data.warnings)
    } else {
      this.hideWarnings()
    }

    // Show preview if available
    if (data.preview && data.preview.destinations && data.preview.destinations.length > 0) {
      this.showPreview(data.preview)
    } else {
      this.hidePreview()
    }

    // Update status indicator
    if (data.valid) {
      this.showValidStatus()
    } else {
      this.showInvalidStatus()
    }
  }

  showLoading() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("hidden")
      this.statusTarget.innerHTML = `
        <div class="flex items-center space-x-2 text-blue-600">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span class="text-sm">Validazione in corso...</span>
        </div>
      `
    }
  }

  hideLoading() {
    // Loading is hidden when status is updated
  }

  showSubmitLoading() {
    this.isSubmitting = true

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    if (!this.hasSubmitButtonTarget) return

    // Guard against a double submit only. The visible feedback is the submit-progress overlay,
    // which covers the page and carries its own translated copy.
    const button = this.submitButtonTarget
    button.classList.add("opacity-75", "cursor-wait", "pointer-events-none")
    button.setAttribute("aria-disabled", "true")
  }

  showErrors(errors) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.remove("hidden")
      const errorsList = errors.map(error => `<li>${this.escapeHtml(error)}</li>`).join("")
      this.errorsTarget.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 class="text-sm font-medium text-red-800 mb-2">Errori di validazione:</h4>
          <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
            ${errorsList}
          </ul>
        </div>
      `
    }
  }

  hideErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.add("hidden")
      this.errorsTarget.innerHTML = ""
    }
  }

  showWarnings(warnings) {
    if (this.hasWarningsTarget) {
      this.warningsTarget.classList.remove("hidden")
      const warningsList = warnings.map(warning => `<li>${this.escapeHtml(warning)}</li>`).join("")
      this.warningsTarget.innerHTML = `
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 class="text-sm font-medium text-yellow-800 mb-2">Avvisi:</h4>
          <ul class="list-disc list-inside text-sm text-yellow-700 space-y-1">
            ${warningsList}
          </ul>
        </div>
      `
    }
  }

  hideWarnings() {
    if (this.hasWarningsTarget) {
      this.warningsTarget.classList.add("hidden")
      this.warningsTarget.innerHTML = ""
    }
  }

  showPreview(preview) {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
      
      const destinationsHtml = preview.destinations.map(dest => {
        let icon = "✓"
        let colorClass = "text-green-600"
        let bgClass = "bg-green-50 border-green-200"
        
        if (dest.action === "error") {
          icon = "✗"
          colorClass = "text-red-600"
          bgClass = "bg-red-50 border-red-200"
        } else if (dest.action === "create_process") {
          icon = "➕"
          colorClass = "text-blue-600"
          bgClass = "bg-blue-50 border-blue-200"
        } else if (dest.action === "add_to_process") {
          icon = "↗"
          colorClass = "text-blue-600"
          bgClass = "bg-blue-50 border-blue-200"
        }
        
        return `
          <div class="${bgClass} border rounded-lg p-3">
            <div class="flex items-center space-x-2">
              <span class="${colorClass} font-semibold">${icon}</span>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">${this.escapeHtml(dest.zone_name)}</p>
                <p class="text-xs text-gray-600">${this.escapeHtml(dest.message)}</p>
              </div>
            </div>
          </div>
        `
      }).join("")
      
      this.previewTarget.innerHTML = `
        <div class="space-y-2">
          <h4 class="text-sm font-medium text-gray-800 mb-2">Anteprima:</h4>
          ${destinationsHtml}
        </div>
      `
    }
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
      this.previewTarget.innerHTML = ""
    }
  }

  showValidStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("hidden")
      this.statusTarget.innerHTML = `
        <div class="flex items-center space-x-2 text-green-600">
          <span>✓</span>
          <span class="text-sm font-medium">Movimento valido</span>
        </div>
      `
    }
  }

  showInvalidStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("hidden")
      this.statusTarget.innerHTML = `
        <div class="flex items-center space-x-2 text-red-600">
          <span>✗</span>
          <span class="text-sm font-medium">Movimento non valido</span>
        </div>
      `
    }
  }

  hideAllFeedback() {
    this.hideErrors()
    this.hideWarnings()
    this.hidePreview()
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden")
      this.statusTarget.innerHTML = ""
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  showError(message) {
    if (this.hasErrorsTarget) {
      this.showErrors([message])
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
